'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { Button } from '@/shared/ui/button';
import { Form } from '@/shared/ui/form';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import './review-modal.scss';

type FormValues = {
    rating: number;
    review: string;
};

type Props = ModalProps & {
    children?: ReactNode;
};

type ReviewModalContextValue = {
    form: UseFormReturn<FormValues>;
    onClose: ModalProps['onClose'];
};

const ReviewModalContext = createContext<ReviewModalContextValue | null>(null);

const useReviewModalContext = () => {
    const context = useContext(ReviewModalContext);
    if (!context) {
        throw new Error('ReviewModal components must be used within ReviewModal.');
    }
    return context;
};

const ReviewModalProvider = ({ children, ...props }: Props) => {
    const form = useForm<FormValues>({
        defaultValues: {
            rating: 0,
            review: '',
        },
    });

    return (
        <ReviewModalContext.Provider
            value={{
                form,
                onClose: props.onClose,
            }}
        >
            {children}
        </ReviewModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const ReviewModalTitle = () => {
    const t = useTranslations();
    return <>{t('REVIEW_MODAL.TITLE')}</>;
};

type ReviewModalFormProps = ComponentProps<typeof Layout>;
const ReviewModalForm = ({ children, media, ...props }: ReviewModalFormProps) => {
    const { form } = useReviewModalContext();
    const handleSubmit = (data: FormValues) => {
        alert(`Submitted review: ${JSON.stringify(data)}`);
    };
    return (
        <Layout
            element={Form}
            media={media}
            {...form}
            noValidate
            onSubmit={form.handleSubmit(handleSubmit)}
            {...props}
        >
            {children}
        </Layout>
    );
};

type StarRatingProps = ComponentProps<typeof Layout>;
const ReviewModalStarRating = ({ media, ...props }: StarRatingProps) => {
    const { form } = useReviewModalContext();
    return (
        <Layout
            element={Form.StarRating}
            media={media}
            {...form}
            name="rating"
            rules={{ required: true, min: 1 }}
            data-testid="review-modal-star-rating"
            {...props}
        />
    );
};

type TextAreaProps = ComponentProps<typeof Layout>;
const ReviewModalTextArea = ({ media, ...props }: TextAreaProps) => {
    const t = useTranslations();
    const { form } = useReviewModalContext();
    return (
        <Layout
            element={Form.TextArea}
            media={media}
            {...form}
            name="review"
            label={t('REVIEW_MODAL.PLACEHOLDER')}
            rows={3}
            data-testid="review-modal-textarea"
            {...props}
        />
    );
};

type PointsMessageButtonProps = ComponentProps<typeof Layout>;
const ReviewModalPointsMessageButton = ({ media, ...props }: PointsMessageButtonProps) => {
    const t = useTranslations();
    return (
        <Layout
            element={Button}
            media={media}
            className="review-modal__review-message-button"
            variant="coupon"
            data-testid="review-modal-points-message-button"
            {...props}
        >
            <Icon name="coupon" />
            {t.rich('REVIEW_MODAL.POINTS_MESSAGE', {
                count: 2,
                b: (chunks) => <b>{chunks}</b>,
            })}
        </Layout>
    );
};

type SubmitButtonProps = ComponentProps<typeof Layout>;
const ReviewModalSubmitButton = ({ media, ...props }: SubmitButtonProps) => {
    const t = useTranslations();
    return (
        <Layout
            element={Button}
            media={media}
            type="submit"
            data-testid="review-modal-submit-button"
            {...props}
        >
            {t('REVIEW_MODAL.SUBMIT_BUTTON')}
        </Layout>
    );
};

export const ReviewModalWrapper = ({ children, ...props }: Props) => {
    return (
        <ReviewModalProvider {...props}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'review-modal',
                    containerProps: {
                        className: 'review-modal__container',
                    },
                    'data-testid': 'review-modal',
                }}
            >
                {children}
            </Modal>
        </ReviewModalProvider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const ReviewModalProviderComponent = ({ children, ...props }: Props) => (
    <ReviewModalProvider {...props}>{children}</ReviewModalProvider>
);

export {
    ReviewModalWrapper as Wrapper,
    ReviewModalProviderComponent as Provider,
    ReviewModalTitle as Title,
    ReviewModalForm as Form,
    ReviewModalStarRating as StarRating,
    ReviewModalTextArea as TextArea,
    ReviewModalPointsMessageButton as PointsMessageButton,
    ReviewModalSubmitButton as SubmitButton,
};

export { ReviewModalWrapper as ReviewModal };
